import { setCheckoutCookie } from '@/lib/checkout/cookies.client';
import { getChannelFromLocale } from '@/lib/configuration/channels';
import { devError } from '@/lib/utils/devLog';

import type { ApiError, CheckoutData } from '../types';

export function isCheckoutBrowserClient(): boolean {
  return globalThis.window !== undefined;
}

/** Router locale from the current URL path (e.g. `us-en`). */
export function getClientPathLocale(): string | undefined {
  if (!isCheckoutBrowserClient()) {
    return undefined;
  }

  const candidate = globalThis.window.location.pathname.split('/').filter(Boolean)[0];
  return candidate && /^[a-z]{2}-[a-z]{2,3}$/.test(candidate) ? candidate : undefined;
}

/**
 * Bind checkoutId to the browser session cookie before a guarded BFF call.
 * OrderApp mutations run client-side without `/api/checkout/lines` Set-Cookie,
 * so refresh would 404 unless the cookie is written first.
 */
export function ensureCheckoutSessionCookie(checkoutId: string, channelSlug?: string): void {
  if (!isCheckoutBrowserClient()) {
    return;
  }

  const locale = getClientPathLocale();
  const resolvedChannel = channelSlug ?? (locale ? getChannelFromLocale(locale) : undefined);

  if (resolvedChannel) {
    setCheckoutCookie(resolvedChannel, checkoutId);
  }
}

type CheckoutBffResponse = {
  success?: boolean;
  data?: { checkout?: CheckoutData | null; isExpired?: boolean };
  errors?: ApiError[];
};

/**
 * POST to a checkout BFF route from the browser and normalize the response.
 */
export async function postCheckoutBff(
  endpoint: string,
  body: Record<string, unknown>,
  fallbackError: ApiError,
  logContext: string
): Promise<{ checkout: CheckoutData | null; errors: ApiError[]; isExpired?: boolean }> {
  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      devError(logContext, `HTTP ${response.status}`);
      return { checkout: null, errors: [fallbackError] };
    }

    const result = (await response.json()) as CheckoutBffResponse;

    if (!result.success) {
      return {
        checkout: null,
        errors: result.errors?.length ? result.errors : [fallbackError],
        isExpired: result.data?.isExpired,
      };
    }

    return {
      checkout: result.data?.checkout ?? null,
      errors: [],
      isExpired: result.data?.isExpired,
    };
  } catch (error) {
    devError(logContext, error);
    return {
      checkout: null,
      errors: [fallbackError],
    };
  }
}
